// A win's score items as the end-of-hand breakdown shows them (design 8 P1): the engine's registry, read
// from the wasm build by gen.sh (romaji first, the English name beneath), and the limit a hand reached.

import registry from "../wasm/score-items.json";
import { Preset } from "../pb/mj/riichi/v1/riichi_pb";
import type { Win } from "../pb/mj/record/v1/record_pb";

const up100 = (n: number) => Math.ceil(n / 100) * 100;

/** What a win pays, before honba and riichi sticks: a ron, the discarder's payment; a tsumo, each seat's: `all` for a dealer's,
 *  else the others' and the dealer's. Worked out from the base points as the rules do, rounded up to the hundred. */
export function handValue(w: Win, dealer: number): { ron: number } | { all: number } | { others: number; dealer: number } {
  const base = w.basePoints;
  const isDealer = w.winner === dealer;
  if (!w.selfDrawn) return { ron: up100(base * (isDealer ? 6 : 4)) };
  if (isDealer) return { all: up100(base * 2) };
  return { others: up100(base), dealer: up100(base * 2) };
}

export interface ItemName {
  kind: "han" | "yakuman" | "dora";
  romaji: string;
  english: string;
  closed: number; // han with a concealed hand (yakuman: yakuman; dora: per tile)
  open: number; // han with an open hand; 0: concealed only
}

const items = registry as Record<string, ItemName>;

export function item(id: number): ItemName {
  return items[String(id)] ?? { kind: "han", romaji: `#${id}`, english: "", closed: 0, open: 0 };
}

export const isYakuman = (id: number) => item(id).kind === "yakuman";

/** The rules reference's groups (design 8 item 8): every item of the registry once, the yaku by their han with
 *  a concealed hand, then the yakuman, then the dora; within a group in registry order. */
export function referenceGroups(): { key: string; han: number; ids: number[] }[] {
  const ids = Object.keys(items).map(Number).sort((a, b) => a - b);
  const yaku = ids.filter((id) => item(id).kind === "han");
  const hans = [...new Set(yaku.map((id) => item(id).closed))].sort((a, b) => a - b);
  return [
    ...hans.map((han) => ({ key: `han-${han}`, han, ids: yaku.filter((id) => item(id).closed === han) })),
    { key: "yakuman", han: 0, ids: ids.filter((id) => item(id).kind === "yakuman") },
    { key: "dora", han: 0, ids: ids.filter((id) => item(id).kind === "dora") },
  ];
}

export type Limit = "" | "mangan" | "haneman" | "baiman" | "sanbaiman" | "yonbaiman" | "kazoe" | "yakuman";

/** The limit a win reached, from its base points (Riichi); `times` > 1 for stacked yakuman. */
export function limitOf(w: Win, preset: Preset): { limit: Limit; times: number } {
  const yakuman = w.items.filter((i) => isYakuman(i.id)).reduce((n, i) => n + i.value, 0);
  if (yakuman > 0) return { limit: "yakuman", times: Math.max(1, Math.round(w.basePoints / 8000)) };
  const b = w.basePoints;
  if (b >= 8000) return { limit: preset === Preset.TENHOU_4P_RED ? "kazoe" : "yonbaiman", times: 1 };
  if (b >= 6000) return { limit: "sanbaiman", times: 1 };
  if (b >= 4000) return { limit: "baiman", times: 1 };
  if (b >= 3000) return { limit: "haneman", times: 1 };
  if (b >= 2000) return { limit: "mangan", times: 1 };
  return { limit: "", times: 1 };
}

// The result sheets' names in the game's own words (design 15 §1.1): each yaku and limit as Japanese tables print it, beside
// its romaji. scripts/fonts.mjs cuts the characters here that the table's words don't use into a face of their own, fetched
// only when a sheet or the rules show one: the table's first visit never pays for them.

/** Each score item of the engine's registry (game/items) by its id. */
export const yakuKanji: Record<number, string> = {
  1: "立直",
  2: "一発",
  3: "門前清自摸和",
  4: "平和",
  5: "一盃口",
  6: "断幺九",
  7: "役牌 白",
  8: "役牌 發",
  9: "役牌 中",
  10: "自風牌",
  11: "場風牌",
  12: "槍槓",
  13: "嶺上開花",
  14: "海底摸月",
  15: "河底撈魚",
  16: "ダブル立直",
  17: "七対子",
  18: "一気通貫",
  19: "三色同順",
  20: "三色同刻",
  21: "対々和",
  22: "三暗刻",
  23: "三槓子",
  24: "混全帯幺九",
  25: "混老頭",
  26: "小三元",
  27: "二盃口",
  28: "混一色",
  29: "純全帯幺九",
  30: "人和",
  31: "清一色",
  40: "天和",
  41: "地和",
  42: "国士無双",
  43: "九蓮宝燈",
  44: "緑一色",
  45: "四暗刻",
  46: "四槓子",
  47: "清老頭",
  48: "字一色",
  49: "大三元",
  50: "小四喜",
  51: "大四喜",
  60: "ドラ",
  61: "裏ドラ",
  62: "赤ドラ",
};

/** The limits a hand reaches (game/items' limitOf), and a yakuman's multiples. */
export const limitKanji: Record<string, string> = {
  mangan: "満貫",
  haneman: "跳満",
  baiman: "倍満",
  sanbaiman: "三倍満",
  yonbaiman: "四倍満",
  kazoe: "数え役満",
  yakuman: "役満",
};
export const multipleKanji = ["", "", "ダブル", "トリプル", "四倍", "五倍", "六倍"];
